import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { of, Observable, combineLatest, throwError } from 'rxjs';
import {
  catchError,
  filter, map, mergeMap, take, tap,
  toArray
} from 'rxjs/operators';

import { AccountTypeENUM } from 'models/account.model';
import { BranchInviteMemberComponent } from 'shared/forms/branch/invite-member/invite-member.component';
import { AccountService } from 'shared/services/account.service';
import { StoreService } from 'shared/services/store.service';
import { SettingsService } from 'shared/services/settings.service';
import { Account, Branch } from 'graphql/models/generated';

@Component({
  selector: 'app-branch-invite-member-modal',
  templateUrl: './invite-member-modal.component.html',
  styleUrls: ['./invite-member-modal.component.scss']
})
export class BranchInviteMemberModalComponent implements OnInit {
  @ViewChild(BranchInviteMemberComponent) branchInviteMemberComponent!: BranchInviteMemberComponent;

  branches: Branch[] = [];
  accountManufacturer: string = '';

  get form(): UntypedFormGroup {
    return this.branchInviteMemberComponent.form;
  }

  constructor(
    private accountService: AccountService,
    private storeService: StoreService,
    private settingsService: SettingsService,
  ) { }

  ngOnInit(): void {
    combineLatest([this.storeService.getAccount(), this.settingsService.getBranches()])
    .pipe(take(1))
    .subscribe(([account, br]: [Account, Branch[]]) => {
      this.accountManufacturer = account.manufacturer;
      this.branches = br;
    });
  }

  onSubmit(): Observable<any> {
    this.branchInviteMemberComponent.setPending(true);

    const branches = this.form.value.branches;
    const role = this.form.value.role ?? 'SUPERADMIN';
    const extractedAccountType = this.form.value.accountType;
    const org = this.form.value.organization;

    const branchFromOrg = this.branches.filter(e => e.organization.id === org)[0];

    return this.storeService.isCurrentAccountDealer()
    .pipe(
      take(1),
      mergeMap((isDealer: boolean) => {
        let hasFailedInvite = false;
        const errors: {email: string, error: "already_exists" | "unexpected_error"}[] = [];
        const accountType = extractedAccountType ?? (isDealer ? AccountTypeENUM.DEALER : AccountTypeENUM.MANUFACTURER);
        return of(...this.form.controls.invitees.value).pipe(
          filter((value: any) => value.email),
          mergeMap((value) => this.accountService.inviteAccount(
            value.email,
            branches ?? [extractedAccountType === 'DEALER' ? branchFromOrg.id : this.accountManufacturer] ?? [],
            role,
            accountType,
            org
          ).pipe(
            map(result => ({
              email: value.email,
              success: true,
              result,
              error: null
            })),
            catchError((error) => {
              hasFailedInvite = true;
              errors.push({ email: value.email, error });
              
              return of({
                email: value.email,
                success: false,
                result: null,
                error: error.graphQLErrors[0].extensions.code
              });
            })
          )),
          toArray(),
          tap((results) => {
            this.storeService.setTableUpdate()
            if (hasFailedInvite) {
              this.branchInviteMemberComponent.setPending(false);
              const alreadyInvitedEmails = results.filter(e => e.error === 'CONFLICT').map(e => e.email);
              const errMsg = this.getAlreadyInvitedErrorMessage(alreadyInvitedEmails);
              throw new Error(errMsg);
            }
          }),
          // Add another catchError here to handle errors from the entire operation
          catchError((error) => {
            console.error('Error in the overall invitation process:', error);
            return throwError(() => error);
          })
        );
      })
    );
  }


  getAlreadyInvitedErrorMessage(emails: string[]) {
    return `Einer oder mehrere der angegebenen Nutzer mit den E-Mail Adressen (${emails.join(', ')}) besitzen bereits einen Zugang zum System. Sollten die Nutzer sich nicht mehr anmelden können, bitten Sie sie die "Passwort vergessen" Funktion zu nutzen.`;
  }
}
